import React from "react";
import {
  StyleSheet,
  Text,
  View,
  TouchableOpacity,
  Platform,
} from "react-native";
import { LocalUser, RemoteUser } from "../../../lib/agora";
import type {
  IAgoraRTCRemoteUser,
  ILocalAudioTrack,
  ILocalVideoTrack,
} from "../../../lib/agora";
import { Ionicons } from "@expo/vector-icons";

type CallVideoTilesProps = {
  variant: "default" | "compact" | "mobile" | "mobileCompact";
  userName: string;
  remoteUsers: IAgoraRTCRemoteUser[];
  remoteNames: Record<string, string>;
  localMicrophoneTrack: ILocalAudioTrack | null;
  localCameraTrack: ILocalVideoTrack | null;
  micOn: boolean;
  cameraOn: boolean;
  onFlagUser: (flaggedUid: string, flaggedName: string) => void;
};

const TILE_SIZE = 510;
const COMPACT_TILE_SIZE = 331;
const MOBILE_TILE_WIDTH = 343;
const MOBILE_TILE_HEIGHT = 252;
const MOBILE_COMPACT_TILE_WIDTH = 163;
const MOBILE_COMPACT_TILE_HEIGHT = 130;

const videoStyle: React.CSSProperties = {
  width: TILE_SIZE,
  height: TILE_SIZE,
  backgroundColor: "#000",
  borderRadius: 10,
  overflow: "hidden",
};

const compactVideoStyle: React.CSSProperties = {
  width: COMPACT_TILE_SIZE,
  height: COMPACT_TILE_SIZE,
  backgroundColor: "#000",
  borderRadius: 10,
  overflow: "hidden",
};

// Shown over a tile while there is no video, instead of a plain black box
// (#142).
function VideoOffPlaceholder({
  style,
  small,
}: {
  style: object;
  small: boolean;
}) {
  return (
    <View
      pointerEvents="none"
      style={[style as object, styles.videoOff]}
      accessibilityLabel="Camera is off"
    >
      <Ionicons name="videocam-off" size={small ? 22 : 36} color="#8E90A6" />
      <Text style={[styles.videoOffText, small && styles.videoOffTextSmall]}>
        Camera is off
      </Text>
    </View>
  );
}

export default function CallVideoTiles({
  variant,
  userName,
  remoteUsers,
  remoteNames,
  localMicrophoneTrack,
  localCameraTrack,
  micOn,
  cameraOn,
  onFlagUser,
}: CallVideoTilesProps) {
  const isCompact = variant === "compact";
  const isMobile = variant === "mobile" || variant === "mobileCompact";
  const isMobileCompact = variant === "mobileCompact";
  const mobileTileWidth = isMobileCompact
    ? MOBILE_COMPACT_TILE_WIDTH
    : MOBILE_TILE_WIDTH;
  const mobileTileHeight = isMobileCompact
    ? MOBILE_COMPACT_TILE_HEIGHT
    : MOBILE_TILE_HEIGHT;

  const tileStyle = isMobile
    ? styles.tileMobile
    : isCompact
      ? styles.tileCompact
      : styles.tile;
  const nameTagStyle = isMobile
    ? isMobileCompact
      ? styles.nameTagMobileCompact
      : styles.nameTagMobile
    : isCompact
      ? styles.nameTagCompact
      : styles.nameTag;
  const tileVideoStyle = isMobile
    ? {
        width: mobileTileWidth,
        height: mobileTileHeight,
        backgroundColor: "#000",
        borderRadius: 14,
        overflow: "hidden",
      }
    : isCompact
      ? compactVideoStyle
      : videoStyle;

  return (
    <>
      <View
        style={[
          tileStyle,
          isMobile && { width: mobileTileWidth, marginHorizontal: 6 },
        ]}
      >
        <Text style={nameTagStyle}>{userName || "You"}</Text>

        {/* LocalUser is a DOM-backed component; it renders its own <video> element */}
        <LocalUser
          audioTrack={localMicrophoneTrack ?? undefined}
          videoTrack={localCameraTrack ?? undefined}
          micOn={micOn}
          cameraOn={cameraOn}
          playAudio={false} // do not play own mic to avoid echo
          playVideo={true} // ensure local video is actually played
          style={tileVideoStyle}
        />
        {!cameraOn && (
          <VideoOffPlaceholder style={tileVideoStyle} small={isMobileCompact} />
        )}
      </View>

      {remoteUsers.map((user) => {
        const uidStr = String(user.uid);
        const nameLabel = remoteNames[uidStr] || `Participant ${uidStr}`;

        return (
          <View
            key={uidStr}
            style={[
              tileStyle,
              isMobile && { width: mobileTileWidth, marginHorizontal: 6 },
            ]}
          >
            <Text style={nameTagStyle}>{nameLabel}</Text>

            <RemoteUser
              user={user}
              playAudio={true}
              playVideo={true} // force video playback if present
              style={tileVideoStyle}
            >
              {Platform.OS === "web" && (
                <button
                  style={{
                    position: "absolute",
                    top: "12px",
                    right: "12px",
                    backgroundColor: "rgba(0, 0, 0, 0.6)",
                    borderRadius: "16px",
                    padding: "4px 8px",
                    flexDirection: "row",
                    alignItems: "center",
                    display: "flex",
                    border: "none",
                    color: "#ff4444",
                    cursor: "pointer",
                    zIndex: 9999,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log(
                      "[CallVideoTiles WEB] Flag button clicked inside RemoteUser for:",
                      nameLabel,
                      "uid:",
                      uidStr,
                    );
                    onFlagUser(uidStr, nameLabel);
                  }}
                >
                  <Ionicons
                    name="flag"
                    size={14}
                    color="#ff4444"
                    style={{ marginRight: 4 }}
                  />
                  <span
                    style={{
                      color: "#fff",
                      fontFamily: "LexendBold",
                      fontSize: "12px",
                    }}
                  >
                    Flag
                  </span>
                </button>
              )}
            </RemoteUser>
            {!user.hasVideo && (
              <VideoOffPlaceholder
                style={tileVideoStyle}
                small={isMobileCompact}
              />
            )}

            {Platform.OS !== "web" && (
              <TouchableOpacity
                style={styles.flagButton}
                onPress={() => {
                  console.log(
                    "[CallVideoTiles NATIVE] Flag button pressed for:",
                    nameLabel,
                    "uid:",
                    uidStr,
                  );
                  onFlagUser(uidStr, nameLabel);
                }}
                accessibilityRole="button"
                accessibilityLabel={`Flag ${nameLabel}`}
              >
                <Ionicons name="flag" size={14} color="#ff4444" />
                <Text style={styles.flagButtonText}>Flag</Text>
              </TouchableOpacity>
            )}
          </View>
        );
      })}
    </>
  );
}

const styles = StyleSheet.create({
  tile: { width: TILE_SIZE, margin: 6, position: "relative" },
  tileCompact: { width: COMPACT_TILE_SIZE, margin: 6, position: "relative" },
  tileMobile: { marginVertical: 8, position: "relative" },
  videoOff: {
    position: "absolute",
    top: 0,
    left: 0,
    backgroundColor: "#15151F",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  videoOffText: {
    color: "#8E90A6",
    fontFamily: "LexendBold",
    fontSize: 14,
  },
  videoOffTextSmall: {
    fontSize: 11,
  },
  nameTag: {
    position: "absolute",
    left: 27.5,
    top: 457.2,
    height: 30,
    opacity: 1,
    fontFamily: "LexendBold",
    fontSize: 26,
    lineHeight: 30,
    fontWeight: "700",
    color: "#fff",
    zIndex: 1,
  },
  nameTagCompact: {
    position: "absolute",
    left: 18,
    bottom: 18,
    height: 24,
    opacity: 1,
    fontFamily: "LexendBold",
    fontSize: 18,
    lineHeight: 24,
    fontWeight: "700",
    color: "#fff",
    zIndex: 1,
  },
  nameTagMobile: {
    position: "absolute",
    left: 14,
    bottom: 12,
    height: 20,
    opacity: 1,
    fontFamily: "LexendBold",
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "700",
    color: "#fff",
    zIndex: 1,
  },
  nameTagMobileCompact: {
    position: "absolute",
    left: 10,
    bottom: 10,
    height: 18,
    opacity: 1,
    fontFamily: "LexendBold",
    fontSize: 12,
    lineHeight: 18,
    fontWeight: "700",
    color: "#fff",
    zIndex: 1,
  },
  flagButton: {
    position: "absolute",
    top: 12,
    right: 12,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    borderRadius: 16,
    paddingHorizontal: 8,
    paddingVertical: 4,
    flexDirection: "row",
    alignItems: "center",
    zIndex: 10,
  },
  flagButtonText: {
    color: "#fff",
    fontSize: 11,
    fontFamily: "LexendBold",
    fontWeight: "700",
    marginLeft: 4,
  },
});
